import { api } from '../api';

export type OrigenCola = 'pidio_contacto' | 'pago_iniciado_sin_aprobar';

/** Canal por el que se generó/envió la oferta MS2. */
export type CanalOferta = 'chatbot' | 'skytel' | 'agente' | 'medicus' | 'IB' | 'OB';

export type EstadoCrmEfectivo =
  | 'sin_asignar'
  | 'en_gestion'
  | 'contactado'
  | 'callback_agendado'
  | 'oferta_enviada'
  | 'pago_iniciado'
  | 'ganado'
  | 'perdido'
  | 'no_contactar';

export interface TelefonoNormalizado {
  e164: string | null;
  waLink: string | null;
  display: string | null;
  valido: boolean;
}

export interface ContactoTmk {
  email?: string;
  celular: TelefonoNormalizado;
  /** Flag "tiene WhatsApp" (declarado/manual). null = sin verificar (Fase 1). */
  tieneWhatsapp: boolean | null;
}

export interface TmkCrmItem {
  legajoId: string;
  numero: string;
  estadoOnboarding: string;
  estadoCrm: EstadoCrmEfectivo;
  origen: OrigenCola;
  titular: {
    nombres: string;
    apellido: string;
    documento: string;
    contacto: ContactoTmk;
  };
  cantidadIntegrantes: number;
  montoMs2SinIva: number;
  planElegido: 'MS2' | 'MS1' | null;
  pidioContacto: boolean;
  solicitadoAt?: string;
  proximaAccionAt?: string;
  ownerNombre?: string;
  ofertaVigente: boolean;
  yaAcepto: boolean;
  /** Plan que TIENE hoy en Binary (espejo ms2Alta, último sync). null si nunca se sincronizó. */
  planActual: 'MS1' | 'MS2' | 'otro' | null;
  /** Cuándo se sincronizó ese plan desde Binary (ISO). */
  planActualSyncedAt?: string;
  /** Estado que reportó SkyTel (inbound). null si todavía no gestionó este contacto. */
  skytel: {
    proceso: string | null;
    conversion: string | null;
    tipificacion: string | null;
    observacion: string | null;
    /** Cantidad de llamados acumulados que reportó SkyTel. */
    cantidadLlamados: number | null;
    lastInboundAt: string | null;
  } | null;
}

/** Espejo del estado en Binary (último sync por SQL*Plus). */
export interface Ms2AltaMirror {
  afiliadoActivo?: boolean;
  plan?: 'MS1' | 'MS2' | 'otro' | null;
  planCodigo?: string | null;
  fechaAltaBinary?: string | null;
  estadoAfi?: string | null;
  syncedAt?: string;
}

export interface TmkCrmPage {
  items: TmkCrmItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DesgloseIntegrante {
  nombre: string;
  edad: number;
  tramo: string;
  ms2SinIva: number;
  basesinIva: number;
  precioSinIva: number;
  precioConIva: number;
}

export interface FichaTmk {
  legajo: { id: string; numero: string; estadoOnboarding: string };
  crm: {
    estado: EstadoCrmEfectivo;
    asignado: boolean;
    owner: { sub: string; nombre?: string; email?: string } | null;
    proximaAccionAt?: string;
    intentosContacto: number;
  };
  eleccion: {
    plan: 'MS2' | 'MS1' | null;
    pidioContacto: boolean;
    decididoAt?: string;
    solicitadoContactoAt?: string;
  };
  titular: {
    id: string;
    nombres: string;
    apellido: string;
    documento: string;
    cuil?: string;
    contacto: ContactoTmk;
  };
  grupo: Array<{ nombre: string; edad: number; parentesco: string }>;
  precioMs2: {
    desglose: DesgloseIntegrante[];
    ms2TotalSinIva: number;
    baseTotalSinIva: number;
    subtotalSinIva: number;
    iva: number;
    totalConIva: number;
    vigencia: string;
    permanenciaMinimaMeses: number;
    moraMaxDias: number;
    planNombre: string;
  };
  oferta: {
    elegible: boolean;
    motivoNoElegible?: string;
    /** Regla relajada para REENVIAR el mismo link (aunque ya haya elegido MS2);
     *  solo la bloquea pago aprobado o estado no ofertable. La usa "Copiar link". */
    reenviableLink: boolean;
    vigente: {
      canal: CanalOferta;
      generadoPor: { nombre?: string; email?: string };
      generadoAt: string;
      expiraAt: string;
      yaAceptada: boolean;
    } | null;
    /**
     * ¿Se envió alguna oferta por CUALQUIER canal (incluso si ya venció)?
     * Sirve para avisar en la ficha que el link ya salió antes — p.ej. por el
     * chatbot — y que el agente no la re-mande a ciegas.
     */
    enviada: {
      canal: CanalOferta;
      agente?: { nombre?: string; email?: string };
      generadoAt: string;
      expiraAt: string;
      aceptadoAt?: string;
      vigente: boolean;
    } | null;
  };
  /** Estado de sincronización con SkyTel/inConcert (null si nunca se enroló). */
  skytel: {
    sincronizado: boolean;
    codeCliente: string;
    contactId: string | null;
    ultimoStatus: string | null;
    lastSyncAt: string | null;
    ultimoEstadoEnviado: { estadoProceso: string; estadoConversion: string } | null;
    ultimoError: string | null;
    // inbound: lo que SkyTel reportó en su última actualización.
    tipificacion: string | null;
    observacion: string | null;
    /** Cantidad de llamados acumulados que reportó SkyTel. */
    cantidadLlamados: number | null;
    estadoExterno: { proceso: string | null; conversion: string | null } | null;
    lastInboundAt: string | null;
  } | null;
  /** Plan/alta del espejo Binary (último sync). Siempre visible, no depende de la consulta en vivo. */
  ms2Alta: Ms2AltaMirror | null;
  /** Estado del pago MS2 del micrositio. null si nunca se inició. */
  pagoEstado: 'pendiente' | 'aprobado' | 'rechazado' | 'cancelado' | null;
  /** Pago MS2 con monto, fecha y atribución (null si nunca inició uno). */
  pago: PagoFicha | null;
}

/** A quién se le atribuye el pago (mismos derivadores que Ventas/Comisiones). */
export interface AtribucionPago {
  canal: 'skytel' | 'IB' | 'OB' | 'medicus' | 'portal' | null;
  canalLabel: string;
  agente: { tipo: 'persona' | 'chatbot' | 'skytel' | 'medicus'; nombre: string; email?: string } | null;
}

export interface PagoFicha {
  estado: 'pendiente' | 'aprobado' | 'rechazado' | 'cancelado';
  monto: number | null;
  fecha: string | null;
  iniciadoAt: string | null;
  atribucion: AtribucionPago | null;
}

export async function getDisponibles(
  page = 1,
  pageSize = 50,
  filtros: CarteraFiltros = {},
): Promise<TmkCrmPage> {
  // En "Disponibles" sólo aplica la elección del titular (el estado CRM no
  // existe antes de tomar el contacto).
  const { data } = await api.get<TmkCrmPage>('/tmk/crm/disponibles', {
    params: { page, pageSize, eleccion: filtros.eleccion, skytelEstado: filtros.skytelEstado },
  });
  return data;
}

export interface CarteraFiltros {
  /** Estado del pipeline CRM (ej. en_gestion, oferta_enviada, ganado...). */
  estado?: string;
  /** Elección del titular: 'MS2' | 'contacto' | 'MS1' | 'sin_decidir'. */
  eleccion?: string;
  /**
   * Estado SkyTel (inbound): 'con_feedback' | 'sin_feedback' o un valor de
   * conversión (ganado, en_oferta, perdido_*, no_contactar, sin_resultado).
   */
  skytelEstado?: string;
}

/** Etiquetas legibles del eje CONVERSIÓN (resultado comercial) de SkyTel. */
export const SKYTEL_CONVERSION_LABEL: Record<string, string> = {
  ganado: 'Ganado',
  en_oferta: 'En oferta',
  sin_resultado: 'Sin resultado',
  perdido_precio: 'Perdido · precio',
  perdido_no_interesa: 'Perdido · no interesa',
  perdido_ilocalizable: 'Perdido · ilocalizable',
  perdido_baja_base: 'Perdido · baja base',
  no_contactar: 'No contactar',
};

/** Etiquetas legibles del eje PROCESO (etapa de gestión) de SkyTel. */
export const SKYTEL_PROCESO_LABEL: Record<string, string> = {
  en_gestion: 'En gestión',
  contactado: 'Contactado',
  callback_agendado: 'Callback agendado',
  oferta_enviada: 'Oferta enviada',
  pago_iniciado: 'Pago iniciado',
  cerrado: 'Cerrado',
};

export async function getMiCartera(
  page = 1,
  pageSize = 50,
  filtros: CarteraFiltros = {},
): Promise<TmkCrmPage> {
  const { data } = await api.get<TmkCrmPage>('/tmk/crm/mi-cartera', {
    params: { page, pageSize, ...filtros },
  });
  return data;
}

export async function getFichaTmk(legajoId: string): Promise<FichaTmk> {
  const { data } = await api.get<FichaTmk>(`/tmk/crm/legajo/${legajoId}/detalle`);
  return data;
}

/** Respuesta del gateway al (re)generar la intención de pago MS2. */
export interface RegenerarPagoResp {
  modo: 'redirect' | 'directo';
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  urlCheckout?: string;
  referenciaExterna?: string;
  intentos: number;
  max: number;
  motivoRechazo?: string;
}

/**
 * Fuerza un link de pago NUEVO para el legajo (no reusa el checkout pendiente).
 * Se usa cuando el link anterior quedó muerto/caducado. Devuelve el urlCheckout
 * fresco para copiar y pasárselo al titular.
 */
export async function regenerarLinkPago(
  legajoId: string,
  /** Solo cuando el titular no tiene email cargado: el server lo persiste en su ficha. */
  email?: string,
): Promise<RegenerarPagoResp> {
  const { data } = await api.post<RegenerarPagoResp>(
    `/tmk/oferta/legajo/${legajoId}/regenerar-pago`,
    email ? { email } : {},
  );
  return data;
}

/** Un pago del gateway de Binary (consultado en vivo). */
export interface BinaryPagoTx {
  estado: 'aprobado' | 'pendiente' | 'rechazado' | 'otro';
  estadoRaw: string;
  importe?: number | null;
  fechaRequest?: string | null;
  fechaCallback?: string | null;
  autorizacion?: string | null;
}

/**
 * Estado REAL del titular para la ficha: separa lo que TIENE (Binary/PMED) de lo
 * que ELIGIÓ (onboarding) y de sus PAGOS. Se consulta en vivo al abrir la ficha;
 * si PMED no responde, `enVivo=false` y los datos de alta caen al último sync.
 */
export interface FichaBinaryEnVivo {
  enVivo: boolean;
  fechaDato?: string | null;
  motivoFallback?: string;
  alta: {
    dadoDeAlta: boolean;
    estadoAfi?: string | null;
    plan: 'MS1' | 'MS2' | 'otro' | null;
    planCodigo?: string | null;
    fechaAlta?: string | null;
  };
  pagoMs2: {
    estado: string | null;
    monto?: number | null;
    iniciadoAt?: string | null;
    actualizadoAt?: string | null;
    intentos: Array<{
      estado: string;
      monto?: number | null;
      at?: string | null;
      motivoRechazo?: string | null;
    }>;
    atribucion?: AtribucionPago | null;
  };
  pagosBinary: BinaryPagoTx[];
  eleccion: {
    plan: 'MS2' | 'MS1' | null;
    pidioContacto: boolean;
    decididoAt?: string | null;
  };
  sintesis: {
    nivel: 'tiene_ms2' | 'ms2_en_proceso' | 'tiene_ms1' | 'sin_alta';
    tono: 'success' | 'info' | 'warning' | 'neutral';
    titulo: string;
    detalle: string;
    accion?: string;
  };
}

/** Consulta en vivo el estado real del titular en Binary/PMED (alta + plan + pagos). */
export async function getBinaryEnVivo(legajoId: string): Promise<FichaBinaryEnVivo> {
  const { data } = await api.get<FichaBinaryEnVivo>(`/tmk/crm/legajo/${legajoId}/binary`);
  return data;
}

export interface ResolverLegajoResultado {
  legajoId: string;
  numero: string;
  estado: string;
  webId: string;
}

/**
 * Resuelve un legajo por codeCliente (webId del titular), número de legajo
 * (GN008841) o DNI → legajoId interno. Lo usa el deep-link del CRM TMK
 * (`/tmk-crm?codeCliente=` / `?legajo=<numero>` / `?dni=`) para abrir la
 * ficha sin que el agente tenga que buscar.
 */
export async function resolverLegajo(params: {
  codeCliente?: string;
  dni?: string;
  /** Número de legajo (GN008841 / PN...) — deep-link `?legajo=<numero>`. */
  legajo?: string;
}): Promise<ResolverLegajoResultado> {
  const { data } = await api.get<ResolverLegajoResultado>('/tmk/crm/resolver', { params });
  return data;
}

export async function claimContacto(legajoId: string): Promise<{ ok: true; ownerNombre?: string }> {
  const { data } = await api.post(`/tmk/crm/legajo/${legajoId}/claim`, {});
  return data;
}

/**
 * Toma FORZADA del contacto (deep-link `?tomarContacto=true`): el agente que abre
 * el link se hace dueño, PISANDO al anterior si lo hubiera. Sin tope de WIP.
 */
export async function tomarContacto(
  legajoId: string,
): Promise<{ ok: true; ownerNombre?: string; piso: boolean }> {
  const { data } = await api.post(`/tmk/crm/legajo/${legajoId}/tomar`, {});
  return data;
}

export async function liberarContacto(legajoId: string): Promise<{ ok: true }> {
  const { data } = await api.post(`/tmk/crm/legajo/${legajoId}/release`, {});
  return data;
}

// ─── Fase 2: interacciones / cadencia / mi día / WhatsApp ───────────────

export const CANALES = ['llamada', 'whatsapp', 'email', 'sms', 'nota'] as const;
export type CanalTmk = (typeof CANALES)[number];

export const DISPOSICIONES = [
  'no_contesta',
  'numero_equivocado',
  'buzon_voz',
  'agendar_callback',
  'contactado_sin_decision',
  'interesado',
  'oferta_enviada',
  'no_interesado',
  'no_localizable',
] as const;
export type DisposicionTmk = (typeof DISPOSICIONES)[number];

export const DISPOSICION_LABEL: Record<DisposicionTmk, string> = {
  no_contesta: 'No contesta',
  numero_equivocado: 'Número equivocado',
  buzon_voz: 'Buzón de voz',
  agendar_callback: 'Agendar callback',
  contactado_sin_decision: 'Contactado, sin decisión',
  interesado: 'Interesado',
  oferta_enviada: 'Oferta enviada',
  no_interesado: 'No interesado',
  no_localizable: 'No localizable',
};

/** Disposiciones que requieren agendar fecha/hora de la próxima acción. */
export const DISPOSICIONES_CON_FECHA: DisposicionTmk[] = ['agendar_callback', 'interesado'];
/** Disposiciones que cierran como "perdido" y exigen elegir un motivo. */
export const DISPOSICIONES_CON_MOTIVO: DisposicionTmk[] = ['no_interesado'];

export const MOTIVOS_PERDIDA = [
  'precio_alto',
  'sin_presupuesto',
  'mal_momento',
  'no_le_interesa_upgrade',
  'ya_tiene_cobertura',
  'ilocalizable',
  'baja_del_plan_base',
  'otro',
] as const;
export type MotivoPerdida = (typeof MOTIVOS_PERDIDA)[number];

export const MOTIVO_PERDIDA_LABEL: Record<MotivoPerdida, string> = {
  precio_alto: 'Precio alto',
  sin_presupuesto: 'Sin presupuesto',
  mal_momento: 'Mal momento',
  no_le_interesa_upgrade: 'No le interesa el upgrade',
  ya_tiene_cobertura: 'Ya tiene cobertura equivalente',
  ilocalizable: 'Ilocalizable',
  baja_del_plan_base: 'Baja del plan base',
  otro: 'Otro',
};

export interface RegistrarInteraccionInput {
  canal: CanalTmk;
  disposicion: DisposicionTmk;
  nota?: string;
  nextActionAt?: string;
  nextActionTipo?: string;
  motivoPerdida?: MotivoPerdida;
  durationSeg?: number;
}

export interface InteraccionItem {
  id: string;
  createdAt?: string;
  tmkSub: string;
  tmkEmail?: string;
  canal: string;
  disposicion: string;
  nota?: string;
  nextActionAt?: string;
  nextActionTipo?: string;
  durationSeg?: number;
}

export interface MiDia {
  vencidos: TmkCrmItem[];
  hoy: TmkCrmItem[];
  sinProximaAccion: TmkCrmItem[];
  disponiblesCount: number;
}

export async function getMiDia(): Promise<MiDia> {
  const { data } = await api.get<MiDia>('/tmk/crm/mi-dia');
  return data;
}

export async function getTimeline(legajoId: string): Promise<InteraccionItem[]> {
  const { data } = await api.get<InteraccionItem[]>(`/tmk/crm/legajo/${legajoId}/interacciones`);
  return data;
}

export async function registrarInteraccion(
  legajoId: string,
  input: RegistrarInteraccionInput,
): Promise<{ ok: true; estado: string; proximaAccionAt?: string }> {
  const { data } = await api.post(`/tmk/crm/legajo/${legajoId}/interaccion`, input);
  return data;
}

export async function setWhatsapp(legajoId: string, tiene: boolean | null): Promise<{ ok: true }> {
  const { data } = await api.post(`/tmk/crm/legajo/${legajoId}/whatsapp`, { tiene });
  return data;
}

// ─── Fase 3: supervisión ────────────────────────────────────────────────

export interface SupervisionAgente {
  ownerSub: string;
  ownerNombre?: string;
  ownerEmail?: string;
  enCartera: number;
  activos: number;
  ganados: number;
  perdidos: number;
  ofertasEnviadas: number;
  /** Pagos aprobados que vinieron de una oferta, atribuidos a quien la originó. */
  ganadosPorOferta: number;
  callbacksVencidos: number;
  interacciones7d: number;
}

export interface SupervisionResumen {
  cola: { total: number; verde: number; ambar: number; rojo: number };
  agentes: SupervisionAgente[];
}

export async function getEmbudo(): Promise<SupervisionResumen> {
  const { data } = await api.get<SupervisionResumen>('/tmk/supervision/embudo');
  return data;
}

export async function reasignarContacto(legajoId: string, ownerSub: string): Promise<{ ok: true }> {
  const { data } = await api.post(`/tmk/supervision/legajo/${legajoId}/reasignar`, { ownerSub });
  return data;
}

export async function liberarVencidos(horas?: number): Promise<{ liberados: number }> {
  const { data } = await api.post('/tmk/supervision/liberar-vencidos', { horas });
  return data;
}

/** Dashboard de supervisión: embudo + total gestionados + conteo por estado CRM. */
export interface SupervisionDashboard extends SupervisionResumen {
  totalGestionados: number;
  porEstado: Record<string, number>;
}

export async function getSupervisionDashboard(): Promise<SupervisionDashboard> {
  const { data } = await api.get<SupervisionDashboard>('/tmk/supervision/dashboard');
  return data;
}

export interface SupervisionContacto {
  legajoId: string;
  numero: string;
  titular: { nombres: string; apellido: string; documento: string };
  estadoCrm: EstadoCrmEfectivo;
  estadoOnboarding: string;
  planElegido: 'MS2' | 'MS1' | null;
  pidioContacto: boolean;
  owner: { sub: string; nombre?: string; email?: string } | null;
  proximaAccionAt?: string;
  ultimoContactoAt?: string;
  ultimaDisposicion?: string;
  /** Estado de SkyTel/inConcert (solo en la vista de cohorte SkyTel). */
  skytel?: {
    codeCliente: string;
    estadoExterno?: { proceso: string | null; conversion: string | null } | null;
    tipificacion?: string | null;
    observacion?: string | null;
    lastInboundAt?: string | null;
  } | null;
}

/** Dashboard SkyTel: cohorte `codeCliente` por estado real + ganado causal/orgánico. */
export interface SkytelSupervisionDashboard {
  total: number;
  porEstado: Record<string, number>;
  ganados: { causal: number; organico: number };
  conInbound: number;
  ofertasEnviadas: number;
  /** Cohorte enrolada sin estado de proceso reportado por SkyTel (esperando gestión). */
  enCola: number;
  /** Cohorte con estado de proceso activo reportado por SkyTel (en gestión real). */
  enGestion: number;
  /** Ofertas MS2 enviadas DESDE SkyTel (origen skytel). */
  ofertasSkytel: number;
  /** Claims sobre la cohorte: algunaVez = reclamados ≥1 vez; unaVez = exactamente 1 (excluye >1). */
  claimeados: { algunaVez: number; unaVez: number };
  /** Funnels por cohorte de ofertas (canal): ofertas → aceptadas → pago → ganado. */
  cohorts: Array<{
    canal: string;
    ofertas: number;
    aceptadas: number;
    pagoIniciado: number;
    ganado: number;
  }>;
  /** Distribución del INBOUND real que reporta SkyTel (lo que SkyTel devuelve). */
  inbound: {
    total: number;
    porProceso: Record<string, number>;
    porConversion: Record<string, number>;
    topTipificaciones: Array<{ tipificacion: string; n: number }>;
    ultimoFeedbackAt: string | null;
  };
}

export async function getSkytelSupervision(): Promise<SkytelSupervisionDashboard> {
  const { data } = await api.get<SkytelSupervisionDashboard>('/tmk/supervision/skytel');
  return data;
}

/** Las 4 etapas del funnel de una oferta. */
export interface FunnelEtapas {
  ofertas: number;
  aceptadas: number;
  pagoIniciado: number;
  ganado: number;
}
export type MetricaFunnel = keyof FunnelEtapas;

/** Funnel de ofertas por origen y por día (cohorte por fecha de generación, horario AR). */
export interface CohortsFunnelPorDia {
  /** Días de la ventana (YYYY-MM-DD), más reciente primero. */
  dias: string[];
  canales: Array<{
    canal: string;
    /** Acumulado de TODAS las ofertas del canal (no solo la ventana). */
    total: FunnelEtapas;
    /** Etapas por día (clave YYYY-MM-DD). */
    porDia: Record<string, FunnelEtapas>;
  }>;
}

export async function getCohortsFunnelPorDia(dias = 7): Promise<CohortsFunnelPorDia> {
  const { data } = await api.get<CohortsFunnelPorDia>('/tmk/supervision/cohorts-funnel-dia', {
    params: { dias },
  });
  return data;
}

export interface SupervisionContactosPage {
  items: SupervisionContacto[];
  total: number;
  page: number;
  pageSize: number;
}

export async function getSupervisionContactos(
  page = 1,
  pageSize = 50,
  filtros: { estado?: string; ownerSub?: string; soloSkytel?: boolean } = {},
): Promise<SupervisionContactosPage> {
  const { data } = await api.get<SupervisionContactosPage>('/tmk/supervision/contactos', {
    params: { page, pageSize, ...filtros },
  });
  return data;
}

/** Edición forzada del estado CRM (god-mode supervisor). */
export async function forzarEstadoContacto(
  legajoId: string,
  estado: EstadoCrmEfectivo,
  motivo?: string,
): Promise<{ ok: true; estado: EstadoCrmEfectivo }> {
  const { data } = await api.post(`/tmk/supervision/legajo/${legajoId}/forzar-estado`, {
    estado,
    motivo,
  });
  return data;
}

/** Un evento del histórico consolidado de un contacto. */
export interface EventoHistorial {
  at?: string;
  tipo: 'asignacion' | 'interaccion' | 'forzado' | 'skytel' | 'oferta' | 'pago';
  accion?: string;
  estado?: string;
  canal?: string;
  disposicion?: string;
  quien?: string;
  por?: string;
  nota?: string;
}

/** Histórico consolidado (asignaciones + cambios de estado) de un contacto. */
export async function getHistorialContacto(legajoId: string): Promise<EventoHistorial[]> {
  const { data } = await api.get<EventoHistorial[]>(`/tmk/crm/legajo/${legajoId}/historial`);
  return data;
}

/** Arma un link wa.me con mensaje pre-cargado (texto plano, URL-encodeado). */
export function waLinkConMensaje(e164: string, mensaje: string): string {
  return `https://wa.me/${e164}?text=${encodeURIComponent(mensaje)}`;
}
